import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { Box, Group, Text, ThemeIcon, useMantineColorScheme } from '@mantine/core';
import { Person } from '../../../models/person.model';

interface CVEmployeeSectionProps {
    person: Person;
}

export function CVEmployeeSection({ person }: CVEmployeeSectionProps) {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    if (!person.employeeId) {
        return null;
    }

    const rows: { label: string; value: string | number }[] = [];

    if (person.currentPositionName) {
        rows.push({ label: 'Position', value: person.currentPositionName });
    }
    if (person.contractTypeName) {
        rows.push({ label: 'Contract Type', value: person.contractTypeName });
    }
    if (person.currentProjectName) {
        rows.push({ label: 'Project', value: person.currentProjectName });
    }
    if (person.currentCostCenterName) {
        rows.push({ label: 'Cost Center', value: person.currentCostCenterName });
    }
    if (person.currentWorkScheduleName) {
        rows.push({ label: 'Work Schedule', value: person.currentWorkScheduleName });
    }
    if (person.allocationPercent != null) {
        rows.push({ label: 'Allocation', value: `${person.allocationPercent}%` });
    }
    if (person.workEndDate) {
        rows.push({ label: 'Work End', value: person.workEndDate });
    }

    if (rows.length === 0) {
        return null;
    }

    const borderColor = isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)';
    const labelColor = isDark ? 'var(--mantine-color-gray-4)' : 'var(--mantine-color-dark-3)';

    return (
        <Box>
            <Group mb="xs" gap="xs">
                <ThemeIcon color="brand" variant="light" size="md">
                    <BriefcaseIcon style={{ width: 16, height: 16 }} />
                </ThemeIcon>
                <Text fw={600}>Current Assignment</Text>
            </Group>

            <Box
                style={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                }}
            >
                {rows.map((row, index) => (
                    <Group
                        key={row.label}
                        justify="space-between"
                        px="sm"
                        py={6}
                        style={{
                            borderBottom: index < rows.length - 1 ? `1px solid ${borderColor}` : 'none',
                        }}
                    >
                        <Text size="xs" c="dimmed" style={{ color: labelColor }}>
                            {row.label}
                        </Text>
                        <Text size="xs" fw={500}>
                            {row.value}
                        </Text>
                    </Group>
                ))}
            </Box>
        </Box>
    );
}
